import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, X, List, Map as MapIcon, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import BeachMap from "@/components/BeachMap";
import BeachCard from "@/components/BeachCard";
import FilterChips from "@/components/FilterChips";
import SearchBar from "@/components/SearchBar";
import SeoHead from "@/components/SeoHead";
import { getAllBeachesForMap, getStates } from "@/lib/api";

const MapPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [beaches, setBeaches] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBeach, setSelectedBeach] = useState(null);
  const [showList, setShowList] = useState(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Filters from URL
  const [filters, setFilters] = useState({
    search: searchParams.get("q") || "",
    state: searchParams.get("state") || "",
    has_parking: searchParams.get("parking") === "true" || undefined,
    has_bathrooms: searchParams.get("bathrooms") === "true" || undefined,
    has_showers: searchParams.get("showers") === "true" || undefined,
    is_accessible: searchParams.get("accessible") === "true" || undefined,
    has_lifeguard: searchParams.get("lifeguard") === "true" || undefined,
    is_pet_friendly: searchParams.get("pets") === "true" || undefined,
  });

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [beachesData, statesData] = await Promise.all([
          getAllBeachesForMap(),
          getStates(),
        ]);
        setBeaches(beachesData.beaches || []);
        setStates(statesData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter beaches
  const filteredBeaches = useMemo(() => {
    return beaches.filter((beach) => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          beach.name.toLowerCase().includes(searchLower) ||
          beach.city.toLowerCase().includes(searchLower) ||
          beach.state.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // State filter
      if (filters.state) {
        const stateSlug = filters.state.toLowerCase().replace(/\s+/g, "-");
        if (beach.state.toLowerCase().replace(/\s+/g, "-") !== stateSlug) return false;
      }

      // Amenity filters
      if (filters.has_parking && beach.has_parking !== true) return false;
      if (filters.has_bathrooms && beach.has_bathrooms !== true) return false;
      if (filters.has_showers && beach.has_showers !== true) return false;
      if (filters.is_accessible && beach.is_accessible !== true) return false;
      if (filters.has_lifeguard && beach.has_lifeguard !== true) return false;
      if (filters.is_pet_friendly && beach.is_pet_friendly !== true) return false;

      return true;
    });
  }, [beaches, filters]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set("q", filters.search);
    if (filters.state) params.set("state", filters.state);
    if (filters.has_parking) params.set("parking", "true");
    if (filters.has_bathrooms) params.set("bathrooms", "true");
    if (filters.has_showers) params.set("showers", "true");
    if (filters.is_accessible) params.set("accessible", "true");
    if (filters.has_lifeguard) params.set("lifeguard", "true");
    if (filters.is_pet_friendly) params.set("pets", "true");
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const handleSearch = (query) => {
    setFilters((prev) => ({ ...prev, search: query }));
  };

  const handleStateChange = (value) => {
    setFilters((prev) => ({ ...prev, state: value === "all" ? "" : value }));
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const clearAllFilters = () => {
    setFilters({
      search: "",
      state: "",
      has_parking: undefined,
      has_bathrooms: undefined,
      has_showers: undefined,
      is_accessible: undefined,
      has_lifeguard: undefined,
      is_pet_friendly: undefined,
    });
  };

  const activeFilterCount = Object.values(filters).filter(
    (v) => v && v !== ""
  ).length;

  // Filter sidebar content
  const FilterContent = () => (
    <div className="space-y-6">
      {/* Search */}
      <div>
        <label className="overline block mb-2">Search</label>
        <SearchBar
          initialValue={filters.search}
          onSearch={handleSearch}
          placeholder="Beach name, city..."
          size="default"
        />
      </div>

      {/* State Select */}
      <div>
        <label className="overline block mb-2">State</label>
        <Select value={filters.state || "all"} onValueChange={handleStateChange}>
          <SelectTrigger className="w-full bg-white" data-testid="state-select">
            <SelectValue placeholder="All States" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All States</SelectItem>
            {states.map((state) => (
              <SelectItem key={state.slug} value={state.slug}>
                {state.name} ({state.beach_count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Amenity Filters */}
      <div>
        <label className="overline block mb-3">Amenities</label>
        <FilterChips filters={filters} onChange={handleFilterChange} />
      </div>

      {/* Clear Filters */}
      {activeFilterCount > 0 && (
        <Button
          variant="ghost"
          onClick={clearAllFilters}
          className="w-full text-[#0F6EA8]"
          data-testid="clear-filters-btn"
        >
          <X className="w-4 h-4 mr-2" />
          Clear All Filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col lg:flex-row" data-testid="map-page">
      <SeoHead
        title="Interactive Beach Access Map - All US Public Beaches"
        description="View 650+ public beach access points on an interactive map of the United States. Zoom in to find beaches near you with details on parking availability, restroom facilities, showers, and wheelchair accessibility. Plan your next beach trip today."
        path="/map"
      />
      {/* Desktop Sidebar */}
      <div className={`hidden lg:flex flex-col w-[400px] border-r border-[rgba(18,50,69,0.1)] bg-[#FAF5EE] ${showList ? "" : "lg:hidden"}`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-[rgba(18,50,69,0.1)]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg text-[#123245] font-['Outfit']">
              Beach Access Points
            </h2>
            <span className="text-sm text-[#4B6A7F]">
              {filteredBeaches.length} results
            </span>
          </div>
          <FilterContent />
        </div>

        {/* Beach List */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3" data-testid="beach-list">
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-24 skeleton rounded-xl" />
                ))}
              </div>
            ) : filteredBeaches.length === 0 ? (
              <div className="text-center py-8 text-[#4B6A7F]">
                No beaches found matching your filters.
              </div>
            ) : (
              filteredBeaches.map((beach) => (
                <div
                  key={beach.id}
                  onClick={() => setSelectedBeach(beach)}
                  className={`cursor-pointer transition-all duration-200 ${
                    selectedBeach?.id === beach.id ? "ring-2 ring-[#0F6EA8] rounded-xl" : ""
                  }`}
                >
                  <BeachCard beach={beach} compact />
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        <BeachMap
          beaches={filteredBeaches}
          selectedBeach={selectedBeach}
          onBeachSelect={setSelectedBeach}
          className="h-full"
        />

        {/* Desktop Toggle List Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowList(!showList)}
          className="hidden lg:flex absolute top-4 left-4 bg-white shadow-md z-[1000]"
          data-testid="toggle-list-btn"
        >
          {showList ? <MapIcon className="w-4 h-4 mr-2" /> : <List className="w-4 h-4 mr-2" />}
          {showList ? "Full Map" : "Show List"}
        </Button>

        {/* Mobile Filter/List Toggle */}
        <div className="lg:hidden absolute bottom-6 left-1/2 -translate-x-1/2 z-[1000] flex gap-2">
          <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
            <SheetTrigger asChild>
              <Button
                className="bg-white text-[#123245] shadow-lg hover:bg-[#F4E7D1]"
                data-testid="mobile-filter-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="ml-2 w-5 h-5 bg-[#0F6EA8] text-white rounded-full text-xs flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[80vh] bg-[#FAF5EE]">
              <SheetHeader>
                <SheetTitle className="font-['Outfit'] text-[#123245]">
                  Filter Beaches
                </SheetTitle>
              </SheetHeader>
              <ScrollArea className="h-full py-4">
                <FilterContent />
                <div className="mt-6">
                  <Button
                    className="w-full bg-[#0F6EA8] text-white"
                    onClick={() => setMobileFilterOpen(false)}
                  >
                    Show {filteredBeaches.length} Results
                  </Button>
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>

          <Button
            className="bg-[#123245] text-white shadow-lg"
            onClick={() => setShowList(!showList)}
            data-testid="mobile-list-btn"
          >
            <List className="w-4 h-4 mr-2" />
            List ({filteredBeaches.length})
          </Button>
        </div>
      </div>

      {/* Mobile List Drawer */}
      <Sheet open={showList && window.innerWidth < 1024} onOpenChange={setShowList}>
        <SheetContent side="bottom" className="h-[60vh] bg-[#FAF5EE] lg:hidden">
          <SheetHeader>
            <SheetTitle className="font-['Outfit'] text-[#123245]">
              {filteredBeaches.length} Beach Access Points
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-full py-4">
            <div className="space-y-3">
              {filteredBeaches.map((beach) => (
                <BeachCard key={beach.id} beach={beach} compact />
              ))}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MapPage;
